import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FlagIcon, AlertCircleIcon } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';
import { useClient } from './ClientContext';
const weeks = [{
  label: 'Week 1',
  phases: [{
    name: 'Brand positioning & structure',
    color: '#DC2626',
    span: 100
  }],
  pitstop: 'Check-in: Confirm positioning, messaging and direction'
}, {
  label: 'Week 2',
  phases: [{
    name: 'Website planning & page structure',
    color: '#F59E0B',
    span: 100
  }],
  pitstop: 'Check-in: Review sitemap and page structure'
}, {
  label: 'Week 3–4',
  phases: [{
    name: 'Design refinement',
    color: '#3B82F6',
    span: 50
  }, {
    name: 'Development',
    color: '#2563EB',
    span: 50
  }],
  pitstop: 'Check-in: Design direction approval and development review'
}, {
  label: 'Week 5',
  phases: [{
    name: 'Video pages & SEO setup',
    color: '#8B5CF6',
    span: 50
  }, {
    name: 'CRM integration',
    color: '#7C3AED',
    span: 50
  }],
  pitstop: 'Check-in: Review video pages, SEO and CRM setup'
}, {
  label: 'Week 6',
  phases: [{
    name: 'Testing & refinements',
    color: '#10B981',
    span: 50
  }, {
    name: 'Launch',
    color: '#059669',
    span: 50
  }],
  pitstop: 'Check-in: Final sign-off and launch'
}];
export function WeeklyTimeline() {
  const {
    companyName
  } = useClient();
  const [hoveredWeek, setHoveredWeek] = useState<number | null>(null);
  const [expandedDisclaimer, setExpandedDisclaimer] = useState(false);
  return <section id="timeline" className="py-20 md:py-28 px-4 md:px-6 bg-surface border-b border-white/[0.06]" data-id="element-291">
      <div className="max-w-7xl mx-auto" data-id="element-292">
        <ScrollReveal data-id="element-293">
          <div className="mb-12 md:mb-16" data-id="element-294">
            <span className="text-accent font-bold tracking-[0.2em] text-xs md:text-sm uppercase block mb-4" data-id="element-295">
              Schedule
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight" data-id="element-296">
              Timeline
            </h2>
            <p className="text-zinc-400 mt-4 md:mt-6 max-w-2xl text-base md:text-lg font-light leading-relaxed" data-id="element-297">
              A focused 6 week delivery with regular check-ins to keep
              everything on track.
            </p>
          </div>
        </ScrollReveal>

        {/* Timeline */}
        <div className="mt-8 md:mt-12 space-y-0 border-t border-white/[0.06]" data-id="element-298">
          {weeks.map((week, weekIndex) => <ScrollReveal key={week.label} delay={weekIndex * 0.1} data-id="element-299">
              <motion.div className="relative border-b border-white/[0.06] hover:bg-white/[0.02] transition-colors" onHoverStart={() => setHoveredWeek(weekIndex)} onHoverEnd={() => setHoveredWeek(null)} data-id="element-300">
                <div className="flex flex-col md:flex-row items-stretch min-h-[100px] md:min-h-[120px] group" data-id="element-301">
                  {/* Week label */}
                  <div className="w-full md:w-48 flex-shrink-0 flex flex-col justify-center p-4 md:p-6 border-b md:border-b-0 md:border-r border-white/[0.06]" data-id="element-302">
                    <motion.div className="text-xs md:text-sm font-bold text-white uppercase tracking-widest" animate={{
                  x: hoveredWeek === weekIndex ? 4 : 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-303">
                      {week.label}
                    </motion.div>
                  </div>

                  {/* Phase bars */}
                  <div className="flex-1 flex flex-col justify-center p-4 md:p-6" data-id="element-304">
                    <div className="flex gap-0 overflow-hidden h-10 md:h-12 w-full" data-id="element-305">
                      {week.phases.map((phase, phaseIndex) => <motion.div key={phase.name} className="relative h-full flex items-center justify-center px-2 md:px-4 cursor-default border-r border-black/20 last:border-r-0" style={{
                    backgroundColor: phase.color,
                    width: `${phase.span}%`
                  }} initial={{
                    scaleX: 0,
                    opacity: 0
                  }} whileInView={{
                    scaleX: 1,
                    opacity: 1
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.5,
                    delay: weekIndex * 0.15 + phaseIndex * 0.1,
                    ease: 'easeOut'
                  }} data-id="element-306">
                          <span className="text-white text-[9px] md:text-xs font-bold uppercase tracking-wider text-center truncate" data-id="element-307">
                            {phase.name}
                          </span>
                        </motion.div>)}
                    </div>

                    {/* Pitstop */}
                    <motion.div className="flex items-center gap-2 md:gap-3 mt-3 md:mt-4" initial={{
                  opacity: 0,
                  x: -10
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: weekIndex * 0.15 + 0.3
                }} data-id="element-308">
                      <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-309">
                        <div className="w-1.5 h-1.5 bg-accent animate-pulse" data-id="element-310" />
                        <FlagIcon className="w-3 h-3 text-accent" data-id="element-311" />
                      </div>
                      <span className="text-[10px] md:text-xs text-zinc-500 font-mono uppercase tracking-wide" data-id="element-312">
                        {week.pitstop}
                      </span>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </ScrollReveal>)}
        </div>

        {/* Disclaimer */}
        <ScrollReveal delay={0.3} data-id="element-313">
          <motion.div className="mt-12 md:mt-16 relative overflow-hidden bg-surface-elevated border border-white/[0.06]" layout data-id="element-314">
            <div className="relative z-10 p-6 md:p-12" data-id="element-315">
              <div className="flex flex-col sm:flex-row items-start gap-4 md:gap-6" data-id="element-316">
                <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 border border-amber-500/20 bg-amber-500/5 flex items-center justify-center" data-id="element-317">
                  <AlertCircleIcon className="w-5 h-5 text-amber-500" data-id="element-318" />
                </div>
                <div className="flex-1" data-id="element-319">
                  <h4 className="text-white font-bold text-base md:text-lg mb-3 md:mb-4 uppercase tracking-tight" data-id="element-320">
                    Disclaimer
                  </h4>
                  <motion.div className="overflow-hidden" animate={{
                  height: expandedDisclaimer ? 'auto' : '48px'
                }} transition={{
                  duration: 0.4,
                  ease: 'easeInOut'
                }} data-id="element-321">
                    <p className="text-zinc-400 leading-relaxed text-sm md:text-base font-light" data-id="element-322">
                      Subject to change depending on start date, scope of work
                      and feedback from the client. Please note that any delays
                      in approval or receipt of feedback will extend the project
                      schedule. Please note that certain stages can be fulfilled
                      in tandem to ensure a speedy turnaround time.
                    </p>
                  </motion.div>
                  <button onClick={() => setExpandedDisclaimer(!expandedDisclaimer)} className="mt-3 md:mt-4 text-accent text-[11px] font-bold tracking-widest uppercase hover:text-white transition-colors flex items-center gap-2" data-id="element-323">
                    {expandedDisclaimer ? 'SHOW LESS' : 'READ MORE'}
                    <motion.span animate={{
                    rotate: expandedDisclaimer ? 180 : 0
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-324">
                      ↓
                    </motion.span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </ScrollReveal>
      </div>
    </section>;
}